import React from "react";
import {ActionRowDropdownButton, Badge, DropdownItem, DropdownItemGroup, DropdownItemGroups, Icon} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {animatePulse} from "@/utils/LoadingUtils";
import {useFormContext, useWatch} from "react-hook-form";
import {CheckIcon} from "@heroicons/react/24/outline";
import {UpdateJobApplicationControlProps} from "@widgets/jobApplications/editor/JobApplicationEditor";
import {UpdatedJobApplication} from "@/data/entities/jobApplication";
import {displayMap} from "@widgets/jobApplications/overview/table/columns/JobApplicationStatusFormatter";


const SetStatusButton = ({initialLoading, loading, submitHandler}: UpdateJobApplicationControlProps) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const {control, setValue} = useFormContext<UpdatedJobApplication>();
    const status = useWatch({
        control,
        name: "status",
    });
    const {color, msgId} = displayMap[status];

    return (
        <ActionRowDropdownButton
            variant={"solid"}
            buttonClassname={animatePulse(initialLoading)}
            color={initialLoading ? "secondary" : color}
            disabled={loading || initialLoading}
            loading={initialLoading || loading}
            props={{
                ...applyTooltip(intl.formatMessage({id: "updateStatus"})),
                "aria-label": intl.formatMessage({id: "updateStatus"}),
            }}
            items={
                <DropdownItemGroups>
                    <DropdownItemGroup>
                        <DropdownItem
                            buttonAction={{as: "groupLabel", actionProps: {}}}
                        >
                            <FormattedMessage id={"newStatus"}/>
                        </DropdownItem>

                        <DropdownItem
                            disabled={status === "OPEN"}
                            icon={status === "OPEN" ? <Icon icon={CheckIcon}/> : "empty"}
                            loading={loading}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: (e, close) => {
                                        e.preventDefault();
                                        setValue("status", "OPEN");
                                        submitHandler();
                                        close();
                                    },
                                },
                            }}
                        >
                            <Badge color={displayMap.OPEN.color}>
                                <FormattedMessage id={displayMap.OPEN.msgId}/>
                            </Badge>
                        </DropdownItem>

                        <DropdownItem
                            disabled={status === "IN_PROGRESS"}
                            icon={status === "IN_PROGRESS" ? <Icon icon={CheckIcon}/> : "empty"}
                            loading={loading}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: (e, close) => {
                                        e.preventDefault();
                                        setValue("status", "IN_PROGRESS");
                                        submitHandler();
                                        close();
                                    },
                                },
                            }}
                        >
                            <Badge color={displayMap.IN_PROGRESS.color}>
                                <FormattedMessage id={displayMap.IN_PROGRESS.msgId}/>
                            </Badge>
                        </DropdownItem>

                        <DropdownItem
                            disabled={status === "CLOSED"}
                            icon={status === "CLOSED" ? <Icon icon={CheckIcon}/> : "empty"}
                            loading={loading}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: (e, close) => {
                                        e.preventDefault();
                                        setValue("status", "CLOSED");
                                        submitHandler();
                                        close();
                                    },
                                },
                            }}
                        >
                            <Badge color={displayMap.CLOSED.color}>
                                <FormattedMessage id={displayMap.CLOSED.msgId}/>
                            </Badge>
                        </DropdownItem>
                    </DropdownItemGroup>
                </DropdownItemGroups>
            }
        >
            <FormattedMessage id={msgId}/>
        </ActionRowDropdownButton>
    );
};

export default SetStatusButton;